import { Flex, MenuProps } from 'antd';
import { MongoAbility } from '@casl/ability';
import { ReactComponent as ViewIcon } from '@Icons/additional/eye.svg';
import { ReactComponent as EditIcon } from '@Icons/additional/edit.svg';
import { ReactComponent as DeleteIcon } from '@Icons/additional/trash.svg';
import { ReactComponent as PlusIcon } from '@Icons/additional/plus.svg';
import { ReactComponent as CrossIcon } from '@Icons/additional/close-cross.svg';
import { ReactComponent as TimeLoading } from '@Icons/additional/time-loading.svg';
import { ReactComponent as CopyIcon } from '@Icons/additional/copy.svg';

import checkIsHideCancelForITReqStatus from './utils/checkIsHideCancelForITReqStatus';
import { TableManageRequestsActionsProps } from './types';
import { DRAFT_STATUSES, FINAL_STATUSES } from './constants';

export const items = ({
  status,
  requestType,
  permissions,
  siteSection,
  onEdit,
  onSubmit,
  onView,
  onCancel,
  onCopy,
  onDelete,
  onApprovalHistory,
}: Omit<TableManageRequestsActionsProps, 'requestNo'> & {
  permissions: MongoAbility;
  siteSection: string | null;
}): MenuProps['items'] => {
  const statusLC = status.toLowerCase();
  const isEss = siteSection === 'ess';

  const common = {
    style: {
      padding: 0,
    },
  };

  const isDraft = DRAFT_STATUSES.includes(statusLC);
  const isSubmitted = ![...DRAFT_STATUSES, ...FINAL_STATUSES].includes(
    statusLC
  );

  const isHideCancelITReq = checkIsHideCancelForITReqStatus(
    requestType,
    status
  );

  const isCancelled = statusLC === 'cancelled';
  const isCompleted = statusLC === 'completed';

  const hideEditIFStatusSubmitted = isSubmitted;

  const viewItem = permissions.can('Get', requestType)
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onView}>
            <ViewIcon /> <span>View</span>
          </Flex>
        ),
        key: '0',
      }
    : null;
  const editItem =
    (isDraft || (isSubmitted && requestType !== 'Material')) &&
    permissions.can('Update', requestType) &&
    !hideEditIFStatusSubmitted
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onEdit}>
              <EditIcon />
              <span> Edit </span>
            </Flex>
          ),
          key: '1',
        }
      : null;
  const submitItem =
    isDraft && permissions.can('Submit', requestType)
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onSubmit}>
              <PlusIcon />
              <span> Submit </span>
            </Flex>
          ),
          key: '2',
        }
      : null;
  const cancelItem =
    (!isEss || (isEss && isSubmitted)) &&
    !isCancelled &&
    !isCompleted &&
    !isHideCancelITReq &&
    permissions.can('Cancel', requestType)
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onCancel}>
              <CrossIcon /> <span>Cancel</span>
            </Flex>
          ),
          key: '3',
        }
      : null;
  const deleteItem =
    isDraft &&
    requestType !== 'Material' &&
    permissions.can('Delete', requestType)
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onDelete}>
              <DeleteIcon />
              <span>Delete</span>
            </Flex>
          ),
          key: '4',
        }
      : null;
  const approvalHistoryItem =
    (!isEss || (isEss && !isDraft)) && permissions.can('History', requestType)
      ? {
          ...common,
          label: (
            <Flex
              className="trojan-action-item"
              gap={15}
              onClick={onApprovalHistory}
            >
              <TimeLoading />
              <span>Approval History</span>
            </Flex>
          ),
          key: '5',
        }
      : null;
  const copyItem =
    !isEss && onCopy && permissions.can('Copy', requestType)
      ? {
          label: (
            <Flex className="trojan-action-space" gap={15} onClick={onCopy}>
              <CopyIcon />
              <span>Copy Request</span>
            </Flex>
          ),
          key: '6',
        }
      : null;

  const output: MenuProps['items'] = [
    viewItem,
    editItem,
    submitItem,
    cancelItem,
    copyItem,
    deleteItem,
    approvalHistoryItem,
  ];

  return output;
};
